import { useEffect, useState } from 'react';
import throttle from 'lodash.throttle';

import { getPrefixedLogger, prefixes } from '@ringcentral/rwc-utils/src/rwc-logger';
import { isMobile } from '@ringcentral/rwc-utils/src/client-detection';

const logger = getPrefixedLogger(prefixes.VC_UTILS, 'useOrientation');

export interface OrientationState {
    angle: number;
    orientation?: string;
    type: string;
}

const useOrientation = (initialState: OrientationState = {} as any) => {
    const [state, setState] = useState(initialState);
    const isModernOrientationApiSupported = 'orientation' in window.screen;

    useEffect(() => {
        if (isModernOrientationApiSupported) return;

        logger.warn('Screen Orientation API is not supported, fallback to resize event');

        const updateOrientation30Fps = throttle(() => {
            const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';

            const isMobilePrimary = isMobile && orientation === 'portrait';

            setState({
                angle: window.orientation || 0,
                orientation,
                type: isMobilePrimary || orientation === 'landscape' ? 'primary' : 'secondary',
            });
        }, 32);

        window.addEventListener('resize', updateOrientation30Fps);
        updateOrientation30Fps();

        return () => {
            window.removeEventListener('resize', updateOrientation30Fps);
        };
    }, [isModernOrientationApiSupported]);

    useEffect(() => {
        if (!isModernOrientationApiSupported) return;

        const screen = window.screen;

        const updateOrientation = () => {
            const { orientation } = screen as any;
            if (orientation.angle !== undefined) {
                const [currentOrientation, currentType] = orientation.type.split('-');
                setState({
                    angle: orientation.angle,
                    orientation: currentOrientation,
                    type: currentType,
                });
            } else if (window.orientation !== undefined) {
                setState({
                    angle: typeof window.orientation === 'number' ? window.orientation : 0,
                    type: '',
                });
            } else {
                setState(initialState);
            }
        };
        screen.orientation.addEventListener('change', updateOrientation);
        updateOrientation();

        return () => {
            screen.orientation.removeEventListener('change', updateOrientation);
        };
    }, [isModernOrientationApiSupported]);

    return state;
};

export default useOrientation;
